import { useToast } from '@chakra-ui/react'
import { doc, updateDoc, setDoc } from 'firebase/firestore'
import isRawDateValid from '../../../../../../utils/isRawDateValid'
import FirestoreUser, {
  AdditionalDetails,
  ClinicalTrial,
} from '../../../../../../types/FirestoreUser'
import { useCallback } from 'react'
import { getEffectiveServices } from '../../../../../../utils/firebaseServiceContext'

const useSaveClinicalTrial = (
  uid: string,
  id: string | undefined,
  study: string,
  startDate: string,
  endDate: string,
  group: string,
  firestoreUser: FirestoreUser,
  update: (partial: Partial<AdditionalDetails>) => void,
  onClose: () => void,
  uuidv4: () => string,
  additionalDetails: AdditionalDetails,
  toast: ReturnType<typeof useToast>,
  handleDeleteTrial: (study: string) => void,
  futureTrialAgreement: boolean
) => {
  const handleSave = useCallback(async () => {
    // BasicDateStringPicker returns values like "0-0-0", "2021-0-0", "2021-10-0" when incomplete
    const isStartDateEmpty =
      !startDate ||
      startDate.startsWith('0-') ||
      startDate.includes('-0-') ||
      startDate.endsWith('-0')

    if (!study || isStartDateEmpty) {
      return toast({
        status: 'error',
        title: 'Error',
        description: 'Study and Start Date are required fields.',
        isClosable: true,
      })
    }

    // BasicDateStringPicker returns values like "0-0-0", "2021-0-0", "2021-10-0" when incomplete
    const isEndDateEmpty =
      !endDate ||
      endDate.startsWith('0-') ||
      endDate.includes('-0-') ||
      endDate.endsWith('-0')

    if (startDate && !isStartDateEmpty && !isRawDateValid(startDate)) {
      return toast({
        status: 'error',
        title: 'Error',
        description:
          'Start Date is in an incorrect format. Please enter in YYYY-MM-DD format.',
        isClosable: true,
      })
    }

    if (endDate && !isEndDateEmpty && !isRawDateValid(endDate)) {
      return toast({
        status: 'error',
        title: 'Error',
        description:
          'End Date is in an incorrect format. Please enter in YYYY-MM-DD format.',
        isClosable: true,
      })
    }

    const newTrial: ClinicalTrial = {
      id: id || uuidv4(), // Use existing ID or generate a new one
      study,
      startDate,
      endDate: endDate || '',
      group: group === 'Interventional' || group === 'Placebo' ? group : '',
    }

    const updatedTrials = id
      ? (firestoreUser.additionalDetails?.clinicalTrials || []).map(
          (trial: ClinicalTrial) => (trial.id === id ? newTrial : trial)
        )
      : [...(firestoreUser.additionalDetails?.clinicalTrials || []), newTrial]

    try {
      if (!uid) return alert('Not logged in!')

      const userRef = doc(getEffectiveServices().firestore, 'users', uid)
      await updateDoc(userRef, {
        'additionalDetails.clinicalTrials': updatedTrials,
        'additionalDetails.futureTrialAgreement': futureTrialAgreement,
      })

      update({ clinicalTrials: updatedTrials })
      await setDoc(
        doc(
          getEffectiveServices().firestore,
          `clinicalTrials/${newTrial.id}`,
        ),
        { ...newTrial, uid },
      )

      toast({
        status: 'success',
        title: 'Saved',
        description: 'Clinical trial details were successfully saved!',
        isClosable: true,
      })
    } catch (e) {
      console.error(e)
      toast({
        status: 'error',
        title: 'Error',
        description: 'An error occurred while saving clinical trial details.',
        isClosable: true,
      })
    }

    onClose()
  }, [
    uid,
    id,
    study,
    startDate,
    endDate,
    group,
    firestoreUser,
    update,
    onClose,
    uuidv4,
    toast,
    futureTrialAgreement,
  ])

  return { handleSave }
}

export default useSaveClinicalTrial
